import breakpoint from '@carvana/helios-layout/breakpoint';
import {
  background,
  border,
  brand,
  text,
  body,
  spacing,
  borderRadius
} from '@carvana/helios-styles';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

const Background = styled.div`
  position: relative;
  padding: ${spacing['4Xl']} ${spacing['2Xl']};
  overflow: hidden;
  margin-bottom: 0;

  ${breakpoint.sm`
    padding: 56px ${spacing['4Xl']} 100px ${spacing['4Xl']};
  `}

  ${breakpoint.md`
    margin-bottom: 56px;
  `}

  ${breakpoint.lg`
    padding: ${spacing['7Xl']} ${spacing['3Xl']} ${spacing['9Xl']} ${spacing['3Xl']};
    margin-bottom: 0;
  `}
`;

const VectorWrap = styled.div`
  position: absolute;
  left: 0;
  top: 180px;
  width: 100%;
  min-height: 144.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  pointer-events: none;

  ${breakpoint.sm`
    min-height: 499px;
    top: 65%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}

  ${breakpoint.lg`
    top: 55%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}

  &.no-image {
    top: 145px;

    ${breakpoint.sm`
      top: 63%;
    `}

    ${breakpoint.lg`
      top: 50%;
    `}
  }

  svg {
    min-width: 807px;
    height: auto;
    display: none;

    ${breakpoint.sm`
      min-width: 1500px;
    `}

    ${breakpoint.lg`
      min-width: 2299px;
    `}
  }

  .vector-xs {
    display: block;
  }

  @media (min-width: 440px) {
    .vector-xs {
      display: none;
    }
    .vector-sm {
      display: block;
    }
  }

  ${breakpoint.lg`
    .vector-sm {
      display: none;
    }
    .vector-lg {
      display: block;
    }
  `}

  ${breakpoint.xl`
    .vector-lg {
      display: none;
    }
    .vector-xl {
      display: block;
    }
  `}
`;

const HeroContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 340px;
  box-sizing: border-box;
  overflow: visible;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${breakpoint.sm`
    padding-top: 8px;
    gap: ${spacing['7Xl']};
  `}

  ${breakpoint.lg`
    padding-top: 20px;
    flex-direction: row;
    align-items: center;
    gap: ${spacing['9Xl']};
    max-width: 1272px;
    margin-left: auto;
    margin-right: auto;
  `}

  ${breakpoint.xl`
    max-width: 1440px;
  `}
`;

const LeftColumnWrapper = styled.div`
  position: relative;
  z-index: 1;

  ${breakpoint.lg`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 108px 0 108px ${spacing['6Xl']};
  `}
`;

const RightColumnWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  z-index: 10;

  ${breakpoint.lg`
    flex-shrink: 0;
    padding-right: ${spacing['6Xl']};
  `}
`;

const Heading = styled.h1`
  text-align: center;
  text-transform: uppercase;
  color: ${text.strong};
  ${brand.xs}

  ${breakpoint.sm`
    ${brand.lg};
  `}

  ${breakpoint.lg`
    text-align: left;
  `}
`;

const Subheading = styled.p`
  ${body.regular.md};
  color: ${text.strong};
  margin-top: ${spacing.xl};
  text-align: center;

  ${breakpoint.sm`
    ${body.regular.xl};
  `}

  ${breakpoint.lg`
    text-align: left;
  `}
`;

const CardWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  margin-top: 0;
  min-width: 340px;
  width: 100%;
  max-width: 516px;
  max-height: 296px;
  z-index: 10;

  ${breakpoint.lg`
    flex-shrink: 0;
    padding: 0;
  `}

  &.no-image {
    margin-top: 62px;
  }
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: ${spacing['2Xl']};
  border: 1px solid ${border.weak};
  border-radius: ${borderRadius.xl};
  background-color: ${background.default};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: center;
  max-height: 296px;

  ${breakpoint.sm`
    padding: 60px ${spacing['4Xl']} ${spacing['3Xl']} ${spacing['4Xl']};
  `}

  ${breakpoint.md`
    width: 516px;
  `}

  & button:not([data-cv-test="heroVinEntryLinkButton"]) {
    width: 100%;
  }
`;

const VehicleImageWrapper = styled.div`
  width: 195px;
  height: 110px;
  position: relative;
  z-index: 20;
  margin-bottom: -48px;

  @media (min-width: 440px) {
    width: 320px;
    height: 180px;
    margin-bottom: -72px;
  }
`;

const ImageStyle: CSSProperties = {
  objectFit: 'cover'
};

export {
  Background,
  Card,
  CardWrapper,
  LeftColumnWrapper,
  Heading,
  Subheading,
  HeroContainer,
  ImageStyle,
  RightColumnWrapper,
  VectorWrap,
  VehicleImageWrapper
};
